import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import GroupsIcon from "@mui/icons-material/Groups";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import SchoolIcon from "@mui/icons-material/School";
import Navbar from "../components/Navbar/Navbar";
import { Base_URL } from "../Client/apiURL";

const emptyMessage = { type: "", text: "" };

const surfaceSx = {
  borderRadius: "8px",
  border: "1px solid #E8E3F1",
  boxShadow: "0 8px 22px rgba(35, 25, 66, 0.05)",
};

const listItemSx = {
  border: "1px solid #ECE7F4",
  borderRadius: "8px",
  p: 1.5,
  backgroundColor: "#fff",
};

const mutedTextSx = { color: "#6B6478", fontSize: 13 };

const primaryButtonSx = {
  backgroundColor: "#6B41CB",
  boxShadow: "none",
  "&:hover": { backgroundColor: "#5633A8", boxShadow: "none" },
};

const outlineButtonSx = {
  borderColor: "#D8CFF0",
  color: "#4D3A75",
  "&:hover": { borderColor: "#6B41CB", backgroundColor: "#F8F5FE" },
};

const openTeacherResults = (studentId, resultType) => {
  const path = `/teacher-mode/students/${studentId}/${resultType}`;
  window.open(path, "_blank", "noopener,noreferrer");
};

const getInitial = (name, fallback = "?") => String(name || fallback).trim().charAt(0).toUpperCase();

const isActiveRelationship = (status) => status === "approved" || status === "pending";

const getRelationshipStatusLabel = (status) => {
  if (status === "approved") return "Already connected";
  if (status === "pending") return "Request already sent";
  if (status === "declined") return "Previously declined";
  if (status === "revoked") return "Access revoked";
  return "";
};

const getRelationshipStatusColor = (status) => {
  if (status === "approved") return "success";
  if (status === "pending") return "warning";
  return "default";
};

const studentMatchesSearch = (student, searchValue) => {
  const query = searchValue.trim().toLowerCase();
  if (!query) return true;

  return [
    student.studentName,
    student.studentEmail,
    student.studentId,
  ]
    .filter((value) => value !== null && value !== undefined)
    .some((value) => String(value).toLowerCase().includes(query));
};

const TeacherMode = () => {
  const [studentId, setStudentId] = useState("");
  const [teacherLinks, setTeacherLinks] = useState([]);
  const [lookupStudent, setLookupStudent] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [message, setMessage] = useState(emptyMessage);
  const [studentView, setStudentView] = useState("approved");
  const [studentSearch, setStudentSearch] = useState("");
  const [isStudentSearchOpen, setIsStudentSearchOpen] = useState(false);
  const [teacherProfile, setTeacherProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileForm, setProfileForm] = useState({
    institutionName: "",
    contactNumber: "",
  });

  const hasTeacherProfile = Boolean(teacherProfile?.hasProfile);
  const showTeacherProfileModal = !profileLoading && !hasTeacherProfile;

  const approvedStudents = useMemo(
    () => teacherLinks.filter((link) => link.status === "approved"),
    [teacherLinks]
  );

  const pendingStudents = useMemo(
    () => teacherLinks.filter((link) => link.status === "pending"),
    [teacherLinks]
  );

  const filteredApprovedStudents = useMemo(
    () => approvedStudents.filter((student) => studentMatchesSearch(student, studentSearch)),
    [approvedStudents, studentSearch]
  );

  const filteredPendingStudents = useMemo(
    () => pendingStudents.filter((student) => studentMatchesSearch(student, studentSearch)),
    [pendingStudents, studentSearch]
  );

  const activeLookupLink = useMemo(() => {
    if (!lookupStudent?.id) return null;
    return teacherLinks.find(
      (link) =>
        Number(link.studentId) === Number(lookupStudent.id) &&
        isActiveRelationship(link.status)
    );
  }, [lookupStudent, teacherLinks]);

  const lookupRelationshipStatus = lookupStudent?.relationshipStatus || activeLookupLink?.status || null;
  const lookupHasActiveRelationship = isActiveRelationship(lookupRelationshipStatus);
  const lookupRelationshipLabel = getRelationshipStatusLabel(lookupRelationshipStatus);

  const studentViews = useMemo(
    () => [
      { id: "approved", label: "Approved", count: approvedStudents.length },
      { id: "pending", label: "Pending", count: pendingStudents.length },
    ],
    [approvedStudents.length, pendingStudents.length]
  );

  const stats = useMemo(
    () => [
      {
        label: "Approved students",
        value: approvedStudents.length,
        icon: <GroupsIcon />,
        tone: "#6B41CB",
      },
      {
        label: "Pending invites",
        value: pendingStudents.length,
        icon: <HourglassTopIcon />,
        tone: "#B7791F",
      },
    ],
    [approvedStudents.length, pendingStudents.length]
  );

  const fetchTeacherData = async () => {
    setLoading(true);
    try {
      const studentsRes = await axios.get(`${Base_URL}/app/users/teacher-access/students`, {
        withCredentials: true,
      });
      if (studentsRes.data.responseCode === 200) {
        setTeacherLinks(studentsRes.data.response || []);
      }
    } catch (error) {
      setMessage({ type: "error", text: "Could not load teacher mode data." });
    } finally {
      setLoading(false);
    }
  };

  const fetchTeacherProfile = async () => {
    setProfileLoading(true);
    try {
      const response = await axios.get(`${Base_URL}/app/users/teacher-access/profile`, {
        withCredentials: true,
      });
      if (response.data.responseCode === 200) {
        const profile = response.data.response || {};
        setTeacherProfile(profile);
        setProfileForm({
          institutionName: profile.institutionName || "",
          contactNumber: profile.contactNumber || "",
        });
        if (profile.hasProfile) {
          await fetchTeacherData();
        }
      } else {
        setMessage({ type: "error", text: response.data.message || "Could not load teacher profile." });
      }
    } catch (error) {
      setMessage({ type: "error", text: "Could not load teacher profile." });
    } finally {
      setProfileLoading(false);
    }
  };

  useEffect(() => {
    fetchTeacherProfile();
  }, []);

  const saveTeacherProfile = async () => {
    const institutionName = profileForm.institutionName.trim();
    const contactNumber = profileForm.contactNumber.trim();

    if (!institutionName) {
      setMessage({ type: "error", text: "Institution name is required." });
      return;
    }
    if (!contactNumber) {
      setMessage({ type: "error", text: "Contact number is required." });
      return;
    }

    setProfileSaving(true);
    setMessage(emptyMessage);
    try {
      const response = await axios.post(
        `${Base_URL}/app/users/teacher-access/profile`,
        { institutionName, contactNumber },
        { withCredentials: true }
      );
      if (response.data.responseCode === 200) {
        setTeacherProfile(response.data.response);
        setMessage({ type: "success", text: "Teacher Mode unlocked." });
        await fetchTeacherData();
      } else {
        setMessage({ type: "error", text: response.data.message || "Could not save teacher profile." });
      }
    } catch (error) {
      setMessage({ type: "error", text: "Could not save teacher profile." });
    } finally {
      setProfileSaving(false);
    }
  };

  const lookupStudentBeforeInvite = async () => {
    setMessage(emptyMessage);
    setLookupStudent(null);
    setLookupLoading(true);
    try {
      const response = await axios.get(
        `${Base_URL}/app/users/teacher-access/lookup?student_id=${studentId}`,
        { withCredentials: true }
      );
      if (response.data.responseCode === 200) {
        setLookupStudent(response.data.response);
      } else {
        setMessage({ type: "error", text: response.data.message || "Student not found." });
      }
    } catch (error) {
      setMessage({ type: "error", text: "Could not find student." });
    } finally {
      setLookupLoading(false);
    }
  };

  const inviteStudent = async () => {
    if (!lookupStudent?.id) return;

    const relationshipStatus = lookupStudent.relationshipStatus || activeLookupLink?.status || null;
    if (isActiveRelationship(relationshipStatus)) {
      setConfirmOpen(false);
      setMessage({
        type: "info",
        text:
          relationshipStatus === "approved"
            ? "Student is already in your teacher panel."
            : "Teacher request already sent.",
      });
      return;
    }

    setMessage(emptyMessage);
    setSendingInvite(true);
    try {
      const response = await axios.post(
        `${Base_URL}/app/users/teacher-access/invite`,
        { student_id: lookupStudent.id },
        { withCredentials: true }
      );
      if (response.data.responseCode === 200) {
        setStudentId("");
        setLookupStudent(null);
        setConfirmOpen(false);
        setMessage({ type: "success", text: response.data.message || "Request sent." });
        await fetchTeacherData();
      } else {
        setMessage({ type: "error", text: response.data.message || "Could not send request." });
      }
    } catch (error) {
      setMessage({ type: "error", text: "Could not send request." });
    } finally {
      setSendingInvite(false);
    }
  };

  const removeLink = async (linkId) => {
    setMessage(emptyMessage);
    try {
      const response = await axios.post(
        `${Base_URL}/app/users/teacher-access/links/${linkId}/remove`,
        {},
        { withCredentials: true }
      );
      if (response.data.responseCode === 200) {
        setMessage({ type: "success", text: response.data.message });
        await fetchTeacherData();
      } else {
        setMessage({ type: "error", text: response.data.message || "Could not remove access." });
      }
    } catch (error) {
      setMessage({ type: "error", text: "Could not remove access." });
    }
  };

  return (
    <>
      <Navbar />
      <Box sx={{ width: "92%", maxWidth: 1280, mx: "auto", pt: { xs: 10, md: 13 }, pb: 5 }}>
        <Stack spacing={2}>
          <Card sx={{ ...surfaceSx, boxShadow: "none" }}>
            <CardContent sx={{ p: { xs: 2, md: 2.5 }, "&:last-child": { pb: { xs: 2, md: 2.5 } } }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
              >
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <SchoolIcon sx={{ color: "#6B41CB" }} />
                    <Typography variant="h4" sx={{ fontWeight: 900, color: "#241B3A", fontSize: { xs: 26, md: 32 } }}>
                      Teacher Mode
                    </Typography>
                  </Stack>
                  <Typography sx={{ color: "#6B6478", mt: 0.5 }}>
                    Student access, approvals, and IELTS/PTE reports.
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {profileLoading && (
                    <Chip
                      icon={<CircularProgress size={14} />}
                      label="Checking access"
                      variant="outlined"
                      sx={{ borderRadius: "8px", borderColor: "#D9CEF4" }}
                    />
                  )}
                  {hasTeacherProfile && stats.map((stat) => (
                    <Chip
                      key={stat.label}
                      icon={stat.icon}
                      label={`${stat.value} ${stat.label}`}
                      sx={{
                        borderRadius: "8px",
                        backgroundColor: `${stat.tone}12`,
                        color: stat.tone,
                        fontWeight: 800,
                        "& .MuiChip-icon": { color: stat.tone },
                      }}
                    />
                  ))}
                  {loading && (
                    <Chip
                      icon={<CircularProgress size={14} />}
                      label="Refreshing"
                      variant="outlined"
                      sx={{ borderRadius: "8px", borderColor: "#D9CEF4" }}
                    />
                  )}
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          {hasTeacherProfile && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "minmax(280px, 0.95fr) minmax(0, 1.95fr)" },
              gap: 2,
              width: "100%",
            }}
          >
            <Box>
              <Card sx={surfaceSx}>
                <CardContent sx={{ p: 2.25 }}>
                  <Stack direction="row" spacing={1.2} alignItems="center">
                    <Box
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius: "8px",
                        display: "grid",
                        placeItems: "center",
                        color: "#6B41CB",
                        backgroundColor: "#F2ECFF",
                      }}
                    >
                      <PersonSearchIcon />
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 900, color: "#241B3A", lineHeight: 1.15 }}>
                        Invite student
                      </Typography>
                      <Typography sx={mutedTextSx}>Search by student ID.</Typography>
                    </Box>
                  </Stack>

                  <Stack spacing={1.25} sx={{ mt: 2 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Student ID"
                      value={studentId}
                      onChange={(event) => {
                        setStudentId(event.target.value.replace(/[^\d]/g, ""));
                        setLookupStudent(null);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" && studentId && !lookupLoading) {
                          lookupStudentBeforeInvite();
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonSearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Button
                      variant="contained"
                      onClick={lookupStudentBeforeInvite}
                      disabled={!studentId || lookupLoading}
                      sx={primaryButtonSx}
                    >
                      {lookupLoading ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Find student"}
                    </Button>
                  </Stack>

                  {lookupStudent && (
                    <Box
                      sx={{
                        mt: 2,
                        p: 1.5,
                        borderRadius: "8px",
                        border: "1px solid #DED4F2",
                        backgroundColor: "#FAF8FF",
                      }}
                    >
                      <Stack spacing={1.4}>
                        <Stack direction="row" spacing={1.25} alignItems="center">
                          <Avatar src={lookupStudent.imageUrl || ""} sx={{ width: 42, height: 42 }}>
                            {getInitial(lookupStudent.name, lookupStudent.id)}
                          </Avatar>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography sx={{ fontWeight: 900, color: "#241B3A" }} noWrap>
                              {lookupStudent.name || `Student ${lookupStudent.id}`}
                            </Typography>
                            <Typography sx={mutedTextSx} noWrap>
                              ID {lookupStudent.id} • {lookupStudent.email}
                            </Typography>
                          </Box>
                        </Stack>
                        {lookupRelationshipLabel && (
                          <Chip
                            size="small"
                            label={lookupRelationshipLabel}
                            color={getRelationshipStatusColor(lookupRelationshipStatus)}
                            sx={{ alignSelf: "flex-start", fontWeight: 800, borderRadius: "8px" }}
                          />
                        )}
                        <Button
                          fullWidth
                          variant="contained"
                          disabled={lookupHasActiveRelationship}
                          onClick={() => {
                            if (!lookupHasActiveRelationship) setConfirmOpen(true);
                          }}
                          sx={primaryButtonSx}
                        >
                          {lookupHasActiveRelationship ? lookupRelationshipLabel : "Send request"}
                        </Button>
                      </Stack>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Box>

            <Box>
              <Card sx={surfaceSx}>
                <CardContent sx={{ p: 0 }}>
                  <Box sx={{ p: 2.25, pb: 1.5 }}>
                    <Stack spacing={1.5}>
                      <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={1.5}
                        justifyContent="space-between"
                        alignItems={{ xs: "stretch", md: "center" }}
                      >
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 900, color: "#241B3A" }}>
                            Student Access
                          </Typography>
                          <Typography sx={mutedTextSx}>Manage report access from one place.</Typography>
                        </Box>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: { xs: "100%", md: "auto" } }}>
                          <Stack
                            direction="row"
                            spacing={0.75}
                            sx={{
                              p: 0.5,
                              borderRadius: "8px",
                              backgroundColor: "#F7F4FC",
                              overflowX: "auto",
                              flex: { xs: 1, md: "0 0 auto" },
                            }}
                          >
                            {studentViews.map((view) => {
                              const selected = studentView === view.id;
                              return (
                                <Button
                                  key={view.id}
                                  size="small"
                                  variant={selected ? "contained" : "text"}
                                  onClick={() => setStudentView(view.id)}
                                  sx={{
                                    minWidth: 94,
                                    borderRadius: "7px",
                                    whiteSpace: "nowrap",
                                    boxShadow: "none",
                                    color: selected ? "#fff" : "#5B516E",
                                    backgroundColor: selected ? "#6B41CB" : "transparent",
                                    "&:hover": {
                                      boxShadow: "none",
                                      backgroundColor: selected ? "#5633A8" : "#EEE7FA",
                                    },
                                  }}
                                >
                                  {view.label} ({view.count})
                                </Button>
                              );
                            })}
                          </Stack>
                          <Tooltip title={isStudentSearchOpen || studentSearch ? "Close search" : "Search students"}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                if (isStudentSearchOpen || studentSearch) {
                                  setStudentSearch("");
                                  setIsStudentSearchOpen(false);
                                } else {
                                  setIsStudentSearchOpen(true);
                                }
                              }}
                              sx={{
                                border: "1px solid #D8CFF0",
                                borderRadius: "8px",
                                color: "#6B41CB",
                                width: 34,
                                height: 34,
                              }}
                            >
                              {isStudentSearchOpen || studentSearch ? <CloseIcon fontSize="small" /> : <PersonSearchIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>
                      <Box
                        sx={{
                          maxHeight: isStudentSearchOpen || studentSearch ? 56 : 0,
                          opacity: isStudentSearchOpen || studentSearch ? 1 : 0,
                          transform: isStudentSearchOpen || studentSearch ? "translateY(0)" : "translateY(-8px)",
                          overflow: "hidden",
                          transition: "max-height 220ms ease, opacity 180ms ease, transform 220ms ease",
                          pointerEvents: isStudentSearchOpen || studentSearch ? "auto" : "none",
                        }}
                      >
                        <TextField
                          fullWidth
                          autoFocus={isStudentSearchOpen}
                          size="small"
                          placeholder="Search by name, email, or student ID"
                          value={studentSearch}
                          onChange={(event) => setStudentSearch(event.target.value)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PersonSearchIcon fontSize="small" />
                              </InputAdornment>
                            ),
                            endAdornment: studentSearch ? (
                              <InputAdornment position="end">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setStudentSearch("");
                                    setIsStudentSearchOpen(false);
                                  }}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </InputAdornment>
                            ) : null,
                          }}
                        />
                      </Box>
                    </Stack>
                  </Box>

                  <Divider />

                  <Box sx={{ p: 2.25 }}>
                    {studentView === "approved" && (
                      <>
                        {approvedStudents.length === 0 ? (
                          <Box sx={{ border: "1px dashed #D8CFF0", borderRadius: "8px", p: 3, textAlign: "center" }}>
                            <GroupsIcon sx={{ color: "#6B41CB", fontSize: 34, mb: 1 }} />
                            <Typography sx={{ fontWeight: 900, color: "#241B3A" }}>No approved students yet</Typography>
                            <Typography sx={{ color: "#6B6478", mt: 0.5 }}>Approved students will appear here.</Typography>
                          </Box>
                        ) : filteredApprovedStudents.length === 0 ? (
                          <Box sx={{ border: "1px dashed #D8CFF0", borderRadius: "8px", p: 3, textAlign: "center" }}>
                            <PersonSearchIcon sx={{ color: "#6B41CB", fontSize: 34, mb: 1 }} />
                            <Typography sx={{ fontWeight: 900, color: "#241B3A" }}>No students match your search</Typography>
                            <Typography sx={{ color: "#6B6478", mt: 0.5 }}>Try a different name, email, or student ID.</Typography>
                          </Box>
                        ) : (
                          <Stack spacing={1}>
                            <Grid container sx={{ px: 1.5, display: { xs: "none", md: "flex" } }}>
                              <Grid item md={5}>
                                <Typography sx={mutedTextSx}>Student</Typography>
                              </Grid>
                              <Grid item md={2}>
                                <Typography sx={mutedTextSx}>Status</Typography>
                              </Grid>
                              <Grid item md={2}>
                                <Typography sx={mutedTextSx}>IELTS</Typography>
                              </Grid>
                              <Grid item md={2}>
                                <Typography sx={mutedTextSx}>PTE</Typography>
                              </Grid>
                              <Grid item md={1} />
                            </Grid>
                            {filteredApprovedStudents.map((student) => (
                              <Box key={student.id} sx={listItemSx}>
                                <Grid container spacing={1.25} alignItems="center">
                                  <Grid item xs={12} md={5}>
                                    <Stack direction="row" spacing={1.25} alignItems="center">
                                      <Avatar src={student.studentImageUrl || ""} sx={{ width: 38, height: 38 }}>
                                        {getInitial(student.studentName, student.studentId)}
                                      </Avatar>
                                      <Box sx={{ minWidth: 0 }}>
                                        <Typography sx={{ fontWeight: 900, color: "#241B3A" }} noWrap>
                                          {student.studentName || `Student ${student.studentId}`}
                                        </Typography>
                                        <Typography sx={mutedTextSx} noWrap>
                                          ID {student.studentId} • {student.studentEmail}
                                        </Typography>
                                      </Box>
                                    </Stack>
                                  </Grid>
                                  <Grid item xs={6} md={2}>
                                    <Chip size="small" label="Approved" color="success" sx={{ borderRadius: "8px", fontWeight: 800 }} />
                                  </Grid>
                                  <Grid item xs={6} md={2}>
                                    <Button
                                      fullWidth
                                      size="small"
                                      variant="contained"
                                      endIcon={<OpenInNewIcon />}
                                      onClick={() => openTeacherResults(student.studentId, "ielts")}
                                      sx={primaryButtonSx}
                                    >
                                      IELTS
                                    </Button>
                                  </Grid>
                                  <Grid item xs={6} md={2}>
                                    <Button
                                      fullWidth
                                      size="small"
                                      variant="outlined"
                                      endIcon={<OpenInNewIcon />}
                                      onClick={() => openTeacherResults(student.studentId, "pte")}
                                      sx={outlineButtonSx}
                                    >
                                      PTE
                                    </Button>
                                  </Grid>
                                  <Grid item xs={6} md={1} sx={{ textAlign: "right" }}>
                                    <Tooltip title="Remove access">
                                      <IconButton size="small" color="error" onClick={() => removeLink(student.id)}>
                                        <DeleteOutlineIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Grid>
                                </Grid>
                              </Box>
                            ))}
                          </Stack>
                        )}
                      </>
                    )}

                    {studentView === "pending" && (
                      <>
                        {pendingStudents.length === 0 ? (
                          <Box sx={{ border: "1px dashed #D8CFF0", borderRadius: "8px", p: 3, textAlign: "center" }}>
                            <HourglassTopIcon sx={{ color: "#B7791F", fontSize: 34, mb: 1 }} />
                            <Typography sx={{ fontWeight: 900, color: "#241B3A" }}>No pending invites</Typography>
                            <Typography sx={{ color: "#6B6478", mt: 0.5 }}>Pending requests will appear here.</Typography>
                          </Box>
                        ) : filteredPendingStudents.length === 0 ? (
                          <Box sx={{ border: "1px dashed #D8CFF0", borderRadius: "8px", p: 3, textAlign: "center" }}>
                            <PersonSearchIcon sx={{ color: "#6B41CB", fontSize: 34, mb: 1 }} />
                            <Typography sx={{ fontWeight: 900, color: "#241B3A" }}>No students match your search</Typography>
                            <Typography sx={{ color: "#6B6478", mt: 0.5 }}>Try a different name, email, or student ID.</Typography>
                          </Box>
                        ) : (
                          <Stack spacing={1}>
                            {filteredPendingStudents.map((student) => (
                              <Box key={student.id} sx={listItemSx}>
                                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} alignItems={{ xs: "stretch", sm: "center" }}>
                                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                                    <Avatar src={student.studentImageUrl || ""} sx={{ width: 38, height: 38 }}>
                                      {getInitial(student.studentName, student.studentId)}
                                    </Avatar>
                                    <Box sx={{ minWidth: 0 }}>
                                      <Typography sx={{ fontWeight: 900, color: "#241B3A" }} noWrap>
                                        {student.studentName || `Student ${student.studentId}`}
                                      </Typography>
                                      <Typography sx={mutedTextSx} noWrap>
                                        ID {student.studentId}
                                      </Typography>
                                    </Box>
                                  </Stack>
                                  <Chip size="small" label="Waiting" color="warning" sx={{ alignSelf: { xs: "flex-start", sm: "center" }, borderRadius: "8px", fontWeight: 800 }} />
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    startIcon={<DeleteOutlineIcon />}
                                    onClick={() => removeLink(student.id)}
                                  >
                                    Cancel
                                  </Button>
                                </Stack>
                              </Box>
                            ))}
                          </Stack>
                        )}
                      </>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>
          )}
        </Stack>
      </Box>

      <Dialog
        open={showTeacherProfileModal}
        maxWidth="xs"
        fullWidth
        disableEscapeKeyDown
        onClose={() => {}}
      >
        <DialogTitle sx={{ fontWeight: 900, color: "#241B3A" }}>Set up Teacher Mode</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#5E5870", mb: 2 }}>
            Please share your institution and contact details before using Teacher Mode.
          </Typography>
          {teacherProfile?.user && (
            <Box sx={{ ...listItemSx, mb: 2, backgroundColor: "#FAF8FF" }}>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Avatar>{getInitial(teacherProfile.user.name, teacherProfile.user.id)}</Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 900, color: "#241B3A" }} noWrap>
                    {teacherProfile.user.name || `User ${teacherProfile.user.id}`}
                  </Typography>
                  <Typography sx={mutedTextSx} noWrap>
                    {teacherProfile.user.email}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          )}
          <Stack spacing={1.5}>
            <TextField
              autoFocus
              fullWidth
              label="Institution name"
              value={profileForm.institutionName}
              onChange={(event) =>
                setProfileForm((current) => ({
                  ...current,
                  institutionName: event.target.value,
                }))
              }
            />
            <TextField
              fullWidth
              label="WhatsApp / contact number"
              value={profileForm.contactNumber}
              onChange={(event) =>
                setProfileForm((current) => ({
                  ...current,
                  contactNumber: event.target.value,
                }))
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            fullWidth
            variant="contained"
            disabled={profileSaving}
            startIcon={profileSaving ? <CircularProgress size={16} color="inherit" /> : <SchoolIcon />}
            onClick={saveTeacherProfile}
            sx={primaryButtonSx}
          >
            {profileSaving ? "Saving" : "Unlock Teacher Mode"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOpen} onClose={() => !sendingInvite && setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>
          {lookupHasActiveRelationship ? lookupRelationshipLabel : "Send teacher request?"}
        </DialogTitle>
        <DialogContent>
          {lookupStudent && (
            <Box sx={{ ...listItemSx, mt: 1, backgroundColor: "#F9F7FE" }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar src={lookupStudent.imageUrl || ""}>{getInitial(lookupStudent.name, lookupStudent.id)}</Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 850, color: "#241B3A" }}>
                    {lookupStudent.name || `Student ${lookupStudent.id}`}
                  </Typography>
                  <Typography sx={mutedTextSx} noWrap>
                    ID {lookupStudent.id} • {lookupStudent.email}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          )}
          <Typography sx={{ mt: 2, color: "#5E5870" }}>
            {lookupHasActiveRelationship
              ? "No new request is needed for this student."
              : "This student will receive a request. You can view their results only after they approve."}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button disabled={sendingInvite} onClick={() => setConfirmOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={sendingInvite || lookupHasActiveRelationship}
            startIcon={sendingInvite ? <CircularProgress size={16} color="inherit" /> : <AssignmentTurnedInIcon />}
            onClick={inviteStudent}
            sx={{ minWidth: 146, backgroundColor: "#6B41CB", "&:hover": { backgroundColor: "#5633A8" } }}
          >
            {sendingInvite ? "Sending" : "Send request"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(message.text)}
        autoHideDuration={3500}
        onClose={() => setMessage(emptyMessage)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={message.type || "info"}
          variant="filled"
          onClose={() => setMessage(emptyMessage)}
          sx={{ width: "100%" }}
        >
          {message.text}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TeacherMode;
